import type { Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { markdownSanitizeSchema } from './markdownSanitizeSchema';
import { rehypeInPageAnchors } from './rehypeInPageAnchors';
import { rehypeSafeTagNames } from './rehypeSafeTagNames';
import { rehypeTrustedEmbeds } from './rehypeTrustedEmbeds';

/** `remark-gfm`, for the CommonMark/GFM copy a backend field carries. */
export const markdownRemarkPlugins: NonNullable<Options['remarkPlugins']> = [remarkGfm];

/**
 * The HTML half of the pipeline every renderer in this directory shares:
 * `rehype-raw` for inline HTML mixed into the copy, `rehype-sanitize` with
 * {@link markdownSanitizeSchema} so that HTML reaches the page only as copy,
 * `rehypeInPageAnchors` so an in-page link follows its target to the name the
 * sanitiser prefixed, `rehypeTrustedEmbeds` so the only iframe left is a video
 * player from a trusted host, and `rehypeSafeTagNames` so a malformed tag
 * degrades to a `span` instead of throwing `Invalid tag` out of a server
 * render.
 *
 * The sanitiser runs on the tree `rehype-raw` parsed, so it sees every element
 * the author typed, whether it arrived as markdown or as HTML. The content is
 * not trusted: an admin account or a supplier feed writes it.
 */
export const markdownRehypePlugins: NonNullable<Options['rehypePlugins']> = [
  rehypeRaw,
  [rehypeSanitize, markdownSanitizeSchema],
  [rehypeInPageAnchors, markdownSanitizeSchema.clobberPrefix ?? ''],
  rehypeTrustedEmbeds,
  rehypeSafeTagNames,
];
